import { useState, useRef, useCallback, useEffect } from 'react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const SILENCE_TIMEOUT_MS = 3000;

export function useVoiceInput() {
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [isSupported] = useState(() => !!SpeechRecognition);

  const recognitionRef = useRef(null);
  const onResultRef = useRef(null);
  const onSpeechDetectedRef = useRef(null);
  const onNoSpeechRef = useRef(null);
  const onInterimUpdateRef = useRef(null);
  const onSpeechEndRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const accumulatedRef = useRef('');
  const hasSpeechRef = useRef(false);
  const lastInterimRef = useRef('');
  const finalizedIndicesRef = useRef(new Set());

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const finalize = useCallback(() => {
    clearSilenceTimer();
    const text = accumulatedRef.current.trim();
    if (text) {
      setFinalTranscript(text);
      setInterimTranscript('');
      onResultRef.current?.(text);
    }
    accumulatedRef.current = '';
    hasSpeechRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
  }, [clearSilenceTimer]);

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      finalize();
    }, SILENCE_TIMEOUT_MS);
  }, [clearSilenceTimer, finalize]);

  useEffect(() => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onspeechstart = () => {
      hasSpeechRef.current = true;
      onSpeechDetectedRef.current?.();
    };

    recognition.onspeechend = () => {
      onSpeechEndRef.current?.();
    };

    recognition.onresult = (event) => {
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          if (!finalizedIndicesRef.current.has(i)) {
            finalizedIndicesRef.current.add(i);
            accumulatedRef.current += transcript;
          }
        } else {
          interim += transcript;
        }
      }

      if (interim) {
        const base = accumulatedRef.current.trim();
        const trimmedInterim = interim.trim();
        // Android sends full-text interims that already include accumulatedRef — don't prepend
        const display = base && trimmedInterim.startsWith(base)
          ? trimmedInterim
          : (accumulatedRef.current + interim).trim();
        lastInterimRef.current = display;
        setInterimTranscript(display);
        onInterimUpdateRef.current?.(display);
      } else {
        lastInterimRef.current = '';
        setInterimTranscript(accumulatedRef.current);
      }

      // Reset silence timer on any new speech activity
      resetSilenceTimer();
    };

    recognition.onend = () => {
      if (accumulatedRef.current.trim()) {
        onResultRef.current?.(accumulatedRef.current.trim());
        accumulatedRef.current = '';
        lastInterimRef.current = '';
        setIsListening(false);
        setInterimTranscript('');
        clearSilenceTimer();
        return;
      }
      if (isMobileDevice && !accumulatedRef.current.trim() && lastInterimRef.current.trim()) {
        const fallbackText = lastInterimRef.current.trim();
        lastInterimRef.current = '';
        setFinalTranscript(fallbackText);
        setInterimTranscript('');
        onResultRef.current?.(fallbackText);
      } else {
        lastInterimRef.current = '';
      }
      setIsListening(false);
      setInterimTranscript('');
      clearSilenceTimer();
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') {
        onNoSpeechRef.current?.();
      } else if (event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
      }
      setIsListening(false);
      setInterimTranscript('');
      clearSilenceTimer();
    };

    recognitionRef.current = recognition;

    return () => {
      clearSilenceTimer();
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [resetSilenceTimer, clearSilenceTimer]);

  const startListening = useCallback((onResult, options) => {
    if (!recognitionRef.current) return;
    onResultRef.current = onResult;
    onInterimUpdateRef.current = options?.onInterimUpdate || null;
    accumulatedRef.current = '';
    hasSpeechRef.current = false;
    finalizedIndicesRef.current = new Set();
    setFinalTranscript('');
    setInterimTranscript('');
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (e) {
      // Already started — ignore
    }
  }, []);

  const stopListening = useCallback(() => {
    clearSilenceTimer();
    if (!recognitionRef.current) return;
    // If there's accumulated text, finalize it
    const text = accumulatedRef.current.trim();
    if (text) {
      setFinalTranscript(text);
      setInterimTranscript('');
      onResultRef.current?.(text);
      accumulatedRef.current = '';
    }
    try {
      recognitionRef.current.stop();
    } catch (e) {
      // Already stopped — ignore
    }
  }, [clearSilenceTimer]);

  const setOnSpeechDetected = useCallback((cb) => {
    onSpeechDetectedRef.current = cb;
  }, []);

  const setOnNoSpeech = useCallback((cb) => {
    onNoSpeechRef.current = cb;
  }, []);

  const setOnSpeechEnd = useCallback((cb) => {
    onSpeechEndRef.current = cb;
  }, []);

  return {
    isSupported,
    isListening,
    interimTranscript,
    finalTranscript,
    startListening,
    stopListening,
    setOnSpeechDetected,
    setOnNoSpeech,
    setOnSpeechEnd,
  };
}
